import {
	AllCommunityModule,
	ColDef,
	ModuleRegistry,
	RowClickedEvent,
	themeAlpine,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box } from 'reflexbox';
import { useTheme } from 'styled-components';

import { ComposeIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Badge, IconWrapper, Input, Pagination, Select, Switch, Table } from '@treams/treams-ui';

import { autoSizeStrategy, defaultColDef } from './constants';

export const targetTypeMapping: Record<string, string> = {
	CONVERSATION_INSIGHTS_EMPLOYEE: 'Inzichten voor medewerker',
	CONVERSATION_INSIGHTS_MANAGER: 'Inzichten voor manager',
	TRANSCRIPTION_SUMMARY: 'Transcriptie',
	NOTE_SUMMARIES: 'Notitie samenvattingen',
	TALKING_POINTS: 'Talking points',
	TALENT_INSIGHTS: 'Talent Insights',
	AI_AGENT: 'AI Agent chat',
};

ModuleRegistry.registerModules([AllCommunityModule]);

const gridDefaultColDef: ColDef = { ...defaultColDef, sortable: false, filter: false };

export default function ChatHistory() {
	const { t } = useTranslation();
	const theme = useTheme();
	const history = useHistory();
	const [filter, setFilter] = useState('');
	const [showDemo, setShowDemo] = useState(false);
	const [workspaceFilter, setWorkspaceFilter] = useState('');
	const [targetType, setTargetType] = useState<string | undefined>(undefined);
	const [pagination, setPagination] = useState<Pagination>({ first: 20 });

	const gridTheme = themeAlpine.withParams({
		accentColor: theme.colors.primary,
		rowHoverColor: theme.colors.lightGray,
	});

	const columnDefs: ColDef[] = useMemo(
		() => [
			{ field: 'workspaceID', headerName: 'Workspace' },
			{
				field: 'user',
				headerName: 'Gebruiker',
				cellRenderer: (params) => `${params.data.user.firstName} ${params.data.user.lastName}`,
			},
			{
				field: 'targetType',
				headerName: 'Onderwerp',
				cellRenderer: (params) => {
					const label = targetTypeMapping[params.data.targetType] ?? params.data.targetType;
					if (!params.data.targetDetails?.employee) return label;
					const isEmployee = params.data.targetDetails.employee.id === params.data.user?.id;
					const role = t(
						isEmployee
							? 'settings.ai-workbench.chat-history.employee'
							: 'settings.ai-workbench.chat-history.manager',
					);
					return `${label}: ${role}`;
				},
			},
			{
				field: 'featureRating',
				headerName: 'Rating',
				maxWidth: 100,
				minWidth: 100,
				cellRenderer: (params) => {
					if (!params.data.featureRating) return null;

					return (
						<Box
							flexGrow={1}
							flexBasis={0}
							display="flex"
							flexDirection="row"
							alignItems="center"
							width="100%"
							height={40}
							justifyContent="start"
							pl={3}
						>
							<Badge color={params.data.featureRating.binaryValue ? 'green' : 'red'} />
							{params.data.featureRating.text && (
								<Box lineHeight={1} ml={1}>
									<IconWrapper size={18} color="darkGray">
										<ComposeIcon />
									</IconWrapper>
								</Box>
							)}
						</Box>
					);
				},
			},
			{
				field: 'createdAt',
				headerName: 'Datum',
				cellRenderer: (params) => moment(params.data.createdAt).format('LLL'),
			},
		],
		[t],
	);

	useEffect(() => {
		const timeout = setTimeout(() => setWorkspaceFilter(filter), 100);
		return () => clearTimeout(timeout);
	}, [filter]);

	useEffect(() => {
		setPagination({ first: 20 });
	}, [workspaceFilter, showDemo, targetType]);

	const onRowClicked = useCallback(
		(event: RowClickedEvent) => {
			if (event.data?.id && event.data?.workspaceID) {
				history.push(`/settings/workbench/history/${event.data.workspaceID}/${event.data.id}`);
			}
		},
		[history],
	);

	const query = graphql.usePromptHistoriesQuery({
		variables: {
			filter: workspaceFilter || undefined,
			includeDemoWorkspaces: showDemo,
			targetType,
			...pagination,
		},
	});

	const rowData = useMemo(
		() => query.data?.promptHistories.edges.map((e) => e.node),
		[query.data?.promptHistories.edges],
	);

	return (
		<Box display="flex" flexDirection="column" flexGrow={1}>
			<Box flexGrow={1} flexBasis={0} display="flex" alignItems="center" mb={2}>
				<Input size="small" value={filter} onChange={setFilter} placeholder={t('common.search')} />
				<Box minWidth={200} ml={4}>
					<Select
						search={false}
						small
						selectedValue={targetType}
						options={[
							{ title: 'Alle onderwerpen', value: undefined },
							{
								title: targetTypeMapping.CONVERSATION_INSIGHTS_EMPLOYEE,
								value: 'CONVERSATION_INSIGHTS_EMPLOYEE',
							},
							{
								title: targetTypeMapping.CONVERSATION_INSIGHTS_MANAGER,
								value: 'CONVERSATION_INSIGHTS_MANAGER',
							},
							{ title: targetTypeMapping.TRANSCRIPTION_SUMMARY, value: 'TRANSCRIPTION_SUMMARY' },
							{ title: targetTypeMapping.NOTE_SUMMARIES, value: 'NOTE_SUMMARIES' },
							{ title: targetTypeMapping.TALKING_POINTS, value: 'TALKING_POINTS' },
							{ title: targetTypeMapping.TALENT_INSIGHTS, value: 'TALENT_INSIGHTS' },
							{ title: targetTypeMapping.AI_AGENT, value: 'AI_AGENT' },
						]}
						onChange={setTargetType}
					/>
				</Box>
				<Box minWidth={250} ml={4}>
					<Switch
						label={t('settings.ai-workbench.include-demo-workspaces')}
						on={showDemo}
						onChange={setShowDemo}
					/>
				</Box>
			</Box>
			<Box flexGrow={1} flexBasis={0} minHeight={890}>
				<AgGridReact
					theme={gridTheme}
					loading={query.loading}
					rowData={rowData}
					columnDefs={columnDefs}
					defaultColDef={gridDefaultColDef}
					autoSizeStrategy={autoSizeStrategy}
					enableCellTextSelection
					onRowClicked={onRowClicked}
					rowStyle={{ cursor: 'pointer' }}
				/>
			</Box>
			<Table.Paging
				pageSize={20}
				pageInfo={query.data?.promptHistories.pageInfo}
				onPaginate={setPagination}
			/>
		</Box>
	);
}
